// One store mirroring the glasshouse `state` shape: the screen router
// (stack capped at 30, goBack with a named fallback), theme, session, the
// server state payload, and the UI-local tab and popover state. Screens
// are pure functions of this store; the shell renders their descriptors
// (spec section 1). Every mutation posts and replaces the payload the
// server returns, so a switch never works silently and the UI never does
// its own arithmetic.
import type { BurdenFields } from "../../shared/burden.js";
import { create } from "zustand";
import { FEED_WINDOW } from "../../shared/feedwindow.js";
import { TOPICS } from "../../shared/ontology.js";
import { areaTopics } from "../../shared/areas.js";
import type { LegalDocId } from "../../shared/legal.js";
import { parsePath, pathFor, type Frame } from "../../shared/routes.js";
import { api, type ExpertLite, type SessionUser } from "./api.js";
import type {
  CardDetail,
  CardView,
  CrossfireQuestionView,
  FeedItemView,
  PlanItemView,
  StatePayload,
} from "./types.js";

// Full expert profile as served by /api/experts/:id.
export interface ExpertDetail {
  expert: Record<string, unknown> & {
    id: string;
    name: string;
    short: string | null;
    role: string | null;
    lens: string | null;
    bio: string | null;
    riskNote: string | null;
    reach: string | null;
    cat: string | null;
  };
  channels: Array<{ id: string; label: string; kind: string; url: string | null; note: string | null }>;
  disclosures: Array<{ id: string; label: string; detail: string | null; source: string | null }>;
  axes: Array<{ id: string; axis: string; position: string; source: string | null }>;
  cards: Array<{ id: string; do: string; chip: string; topicId: string | null; cadence: string | null; image: string | null; burden: BurdenFields }>;
}

// Everything else is the signed-in app: reaching it signed out lands on the
// front door instead of a shell that can never load.
const PUBLIC_SCREENS = new Set([
  "welcome",
  "signin",
  "onb-areas",
  "onb-experts",
  "onb-email",
  "about",
  "contact",
  "privacy",
  "terms",
  "health",
]);

/* Routing moved to ../../shared/routes.ts on 2 Aug 2026 so the server can read
   the same table (it answers the first request for every client route and has
   to know which screen the URL names). Re-exported here because the screens
   import these from the store and a rename across forty call sites would be
   churn with no reader on the other end. */
export { pathFor, parsePath, screenForPath, type Frame } from "../../shared/routes.js";

export type Theme = "light" | "dark";

function storedTheme(): Theme {
  // Light is the default until a choice is saved (2 Aug 2026, Rob,
  // superseding the 28 Jul dark default); never inferred from OS preference
  // in either direction. Must agree with public/theme-init.js, which answers
  // the same question before first paint: if the two ever disagree the page
  // paints one theme and swaps to the other when React mounts.
  try {
    return localStorage.getItem("practice-theme") === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

// Onboarding answers live client-side until the magic link round-trip
// creates the account; they are stashed before the link is sent and flushed
// to /api/onboarding on the first signed-in boot.
export interface OnboardingState {
  areas: Record<string, boolean>;
  topics: Record<string, boolean>;
  // Topic ids the reader moved by hand. An area seeds a switch; a hand owns
  // it (1 Aug 2026). Without this, unticking an area would silently undo a
  // choice someone made deliberately in the advanced panel.
  touched: string[];
  follows: string[];
}

const ONBOARDING_STASH = "practice-pending-onboarding";

/* The topic switches implied by the areas ticked so far, layered over the
   ontology defaults, with anything the reader has touched left exactly as
   they left it. Recomputed from scratch on every area toggle rather than
   accumulated, so unticking an area takes its untouched seeds back off. */
function seedTopics(
  areas: Record<string, boolean>,
  current: Record<string, boolean>,
  touched: string[],
): Record<string, boolean> {
  const seeded = new Set(areaTopics(areas));
  const topics: Record<string, boolean> = {};
  for (const t of TOPICS) {
    topics[t.id] = touched.includes(t.id)
      ? Boolean(current[t.id])
      : t.defaultOn || (t.filterable && seeded.has(t.id));
  }
  return topics;
}

function defaultOnboarding(): OnboardingState {
  const topics: Record<string, boolean> = {};
  for (const t of TOPICS) topics[t.id] = t.defaultOn;
  return { areas: {}, topics, touched: [], follows: [] };
}

// Modal sheets (the glasshouse sheetHost): exactly one open at a time.
export type Sheet =
  | { kind: "photo-credits" }
  | { kind: "expert-intro"; id: string }
  | { kind: "expert-peek"; id: string }
  | { kind: "practice"; cardId: string }
  | { kind: "create-practice"; cardId?: string } // cardId set = editing
  /* The whole disagreement (2 Aug 2026, Rob). Crossfire used to render every
     question in full down the page, which put four prose blocks, every lane
     and a practice list between one question and the next. The screen is a
     list now and this is where a question opens, the same move Discover made
     when the detail screen became the practice modal. */
  | { kind: "crossfire"; questionId: string }
  | { kind: "lane"; questionId: string; laneId: string }
  | { kind: "report"; subjectType: "practice" | "expert"; subjectId: string }
  // The company documents are modals rather than destinations (1 Aug 2026,
  // Rob). Reading the terms from the signup screen used to navigate away
  // from a half-typed address; About and Contact hang off the same footer,
  // which is on every screen. The routes still exist for direct links.
  | { kind: "legal"; doc: LegalDocId }
  | { kind: "company"; page: CompanyPageId };

// Declared here rather than beside the prose so the store never has to import
// a screen: everything in the client points at the store, not back.
export type CompanyPageId = "about" | "contact";

/* Where the open sheet is scrolled to, read off the DOM at the moment a child
   sheet is opened from inside it. The store does not track scroll and should
   not: it is a property of a rendered element, and one read at one instant is
   cheaper and truer than keeping it in sync. */
export function sheetScrollNow(): number {
  return document.querySelector(".sheet")?.scrollTop ?? 0;
}

/* The document routes are doors, not destinations (2 Aug 2026, Rob). /terms,
   /privacy, /health-data, /about and /contact each open their own modal over
   wherever the reader belongs: the app when they are signed in, the front
   door when they are not. They had solo pages of their own until now, which
   meant a link to the privacy policy took a signed-in reader out of the app
   and left them to find their way back.

   The addresses stay real, because a policy needs one a link can point at
   from an email or a store listing. What they no longer have is a page that
   replaces whatever the reader was doing. */
const DOC_SHEETS: Record<string, Sheet> = {
  terms: { kind: "legal", doc: "terms" },
  privacy: { kind: "legal", doc: "privacy" },
  health: { kind: "legal", doc: "health" },
  about: { kind: "company", page: "about" },
  contact: { kind: "company", page: "contact" },
};

export interface Landing {
  screen: string;
  arg: string | null;
  sheet: Sheet | null;
}

/* Where a URL actually lands. A document route resolves to the app plus its
   sheet; boot sends a signed-out reader on to the front door from there,
   exactly as it already does for "/", and the sheet rides along untouched. */
export function landingFor(pathname: string): Landing {
  const frame = parsePath(pathname);
  const sheet = DOC_SHEETS[frame.s];
  return sheet ? { screen: "today", arg: null, sheet } : { screen: frame.s, arg: frame.a, sheet: null };
}

// True for the five paths above: used to put the address bar back to the
// screen underneath once the reader closes the document.
function isDocPath(pathname: string): boolean {
  return Boolean(DOC_SHEETS[parsePath(pathname).s]);
}

/* The address a document link points at. Every one of these is a real place,
   so the links to them are real anchors: they can be opened in a new tab,
   copied, and read by anything that reads links, and the click handler opens
   the modal in place for the reader who just wants to look. The ids double as
   the screen ids in PATHS, which is what makes this a lookup rather than a
   second table to keep in step. */
export function docPath(id: LegalDocId | CompanyPageId): string {
  return pathFor(id, null);
}

// Scoped surface search (spec section 5, "Search on a surface"): defaults
// to the people you follow, resets on every screen entry.
export type SearchScope = "follows" | "all";

export interface PracticeSearchHit {
  cardId: string;
  state: "new" | "added" | "later" | "passed";
  until: string | null;
  followed: boolean;
}

export interface PracticeSearch {
  results: PracticeSearchHit[];
  total: number;
  hidden: number;
  cards: Record<string, CardView>;
}

// Popover menus (the glasshouse menuHost): snooze with a real date, no
// thanks with a stated scope, positioned near the button that opened them.
// planItemId marks a plan context: the practice is stopped first, so Later
// and No thanks mean the same thing on an active practice as on a
// suggestion (it leaves the plan and, for Later, returns on its date).
export type Popover =
  | { kind: "snooze"; cardId: string; x: number; y: number; duration?: string; planItemId?: string }
  | { kind: "decline"; cardId: string; x: number; y: number; planItemId?: string }
  // Later for your own practice: same durations, no reasons step (the
  // reasons steer what reaches you, and nothing about your own practice is
  // being steered), pauses in place instead of stopping.
  | { kind: "pause"; planItemId: string; x: number; y: number }
  | { kind: "note"; title: string; body: string };

async function postJson(url: string, body: unknown): Promise<unknown> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

export interface AppStore {
  screen: string;
  arg: string | null;
  back: Frame[];
  theme: Theme;
  menuOpen: boolean;
  searchOpen: boolean;
  sheet: Sheet | null;
  popover: Popover | null;
  user: SessionUser | null;
  sessionLoaded: boolean;
  experts: ExpertLite[] | null;
  onboarding: OnboardingState;
  linkSent: string | null;

  appState: StatePayload | null;
  crossfire: CrossfireQuestionView[] | null;
  feed: { items: FeedItemView[]; saved: string[]; read: string[] } | null;
  cardDetails: Record<string, CardDetail>;
  expertDetails: Record<string, ExpertDetail>;

  // UI-local screen state, mirroring glasshouse.
  discTab: string;
  planDomain: string;
  planTopic: string | null;
  xfTab: string;
  followCat: string;
  followQ: string;
  feedFilter: string;

  // Scoped surface search: query + scope per surface, reset on every nav so
  // Everyone scope is always a fresh, explicit choice.
  discoverQ: string;
  discoverScope: SearchScope;
  // One person's practices, followed or not; naming a source makes scope
  // moot (it is already an explicit choice, same rule as the feed picker).
  // Outside a search it narrows the lanes in place rather than opening the
  // search view (31 Jul 2026, Rob).
  discoverExpert: string | null;
  // What the source was when the search began, so leaving the search hands
  // the page back the one it had rather than the one the search named.
  discoverSourceBefore: string | null;
  feedSourceBefore: string | null;
  practiceSearch: PracticeSearch | null;
  feedQ: string;
  feedScope: SearchScope;
  feedSearch: FeedItemView[] | null;
  xfQ: string;
  xfScope: SearchScope;

  nav(id: string, arg?: string | null): void;
  goBack(fallback?: string, fallbackArg?: string | null): void;
  flipTheme(): void;
  setTimezone(tz: string | null): Promise<void>;
  setMenu(open: boolean): void;
  setSearchOpen(open: boolean): void;
  /* `under` is what to come back to when this one closes, with the scroll
     position it had (2 Aug 2026, Rob: open a practice from a crossfire
     question, close it, and be back where you were so the next practice is
     one press away). One level, not a stack: the case is a modal that lists
     things, and a stack would let a reader burrow somewhere they cannot see
     the way out of. Opening without it clears any return, so a sheet reached
     from a screen never resurrects an unrelated one. */
  openSheet(sheet: Sheet, under?: { sheet: Sheet; scroll: number }): void;
  closeSheet(): void;
  sheetUnder: { sheet: Sheet; scroll: number } | null;
  // Consumed by the sheet on mount: the scroll to restore, once.
  sheetScroll: number | null;
  clearSheetScroll(): void;
  openPopover(p: Popover): void;
  closePopover(): void;
  setDiscTab(t: string): void;
  setPlanDomain(d: string): void;
  setPlanTopic(t: string | null): void;
  setXfTab(t: string): void;
  setFollowCat(c: string): void;
  setFollowQ(q: string): void;
  setFeedFilter(f: string): void;
  setDiscoverQ(q: string): void;
  setDiscoverScope(s: SearchScope): void;
  setDiscoverExpert(id: string | null): void;
  setFeedQ(q: string): void;
  setFeedScope(s: SearchScope): void;
  setXfQ(q: string): void;
  setXfScope(s: SearchScope): void;
  runPracticeSearch(): Promise<void>;
  runFeedSearch(): Promise<void>;

  toggleArea(id: string): void;
  toggleObTopic(id: string): void;
  toggleObFollow(id: string): void;
  finishOnboarding(
    email: string,
    inviteCode: string,
  ): Promise<{ ok: boolean; error?: string; needsInvite?: boolean }>;
  requestSignIn(email: string): Promise<{ ok: boolean; error?: string }>;

  refreshState(): Promise<void>;
  mutate(url: string, body: unknown): Promise<void>;
  loadCrossfire(): Promise<void>;
  loadFeed(): Promise<void>;
  // One expert's feed, followed or not; null is everyone you follow.
  feedExpert: string | null;
  setFeedExpert(id: string | null): void;
  loadCardDetail(id: string): Promise<void>;
  loadExpertDetail(id: string): Promise<void>;
  addCard(cardId: string, version: string, when: string | null): Promise<void>;
  toggleComplete(planItemId: string, date: string, done: boolean): Promise<void>;
  markAll(planItemIds: string[], date: string, done: boolean): Promise<void>;
  // The dragged running order. Takes the ids of the visible list only, in
  // their new order; the rest of the plan holds its place.
  reorderPlan(planItemIds: string[]): Promise<void>;
  snoozeCard(cardId: string, duration: string, reason: string | null, planItemId?: string): Promise<void>;
  unsnooze(cardId: string): Promise<void>;
  declineCard(cardId: string, scope: string, reason: string | null, planItemId?: string): Promise<void>;
  undecline(declineId: string): Promise<void>;
  // Your own practices: create/edit/delete, pause (a break, not a delete),
  // and the optional photo.
  pausePlan(planItemId: string, duration: string): Promise<void>;
  createPractice(fields: { do: string; cadenceId: string; note: string | null }): Promise<string | null>;
  updatePractice(fields: { cardId: string; do?: string; cadenceId?: string; note?: string | null; removeImage?: boolean }): Promise<void>;
  removePractice(cardId: string): Promise<void>;
  uploadPracticeImage(cardId: string, file: File): Promise<boolean>;
  setAreas(areas: string[]): Promise<void>;
  reportIssue(subjectType: "practice" | "expert", subjectId: string, reason: string, detail: string | null): Promise<boolean>;
  setTopic(topicId: string, on: boolean): Promise<void>;
  setMute(expertId: string, topicId: string, muted: boolean): Promise<void>;
  toggleFollow(expertId: string): void;
  pickStance(questionId: string, pick: string, note?: string | null): Promise<void>;
  saveFeedItem(feedItemId: string, saved: boolean): Promise<void>;
  markFeedRead(feedItemId: string, read: boolean): Promise<void>;
  setPlanVersion(planItemId: string, version: string): Promise<void>;
  setPlanStatus(planItemId: string, status: string): Promise<void>;

  boot(): Promise<void>;
  signOut(): Promise<void>;
}

// Read once: the first URL decides the screen and, on a document route, the
// sheet that opens over it.
const landing = landingFor(location.pathname);

export const useStore = create<AppStore>((set, get) => ({
  screen: landing.screen,
  arg: landing.arg,
  back: [],
  theme: storedTheme(),
  menuOpen: false,
  searchOpen: false,
  sheetUnder: null,
  sheetScroll: null,
  sheet: landing.sheet,
  popover: null,
  user: null,
  sessionLoaded: false,
  experts: null,
  onboarding: defaultOnboarding(),
  linkSent: null,
  appState: null,
  crossfire: null,
  feed: null,
  feedExpert: null,
  cardDetails: {},
  expertDetails: {},
  discTab: "new",
  planDomain: "all",
  planTopic: null,
  xfTab: "new",
  followCat: "recommended",
  followQ: "",
  feedFilter: "new",
  discoverQ: "",
  discoverScope: "follows",
  discoverExpert: null,
  discoverSourceBefore: null,
  feedSourceBefore: null,
  practiceSearch: null,
  feedQ: "",
  feedScope: "follows",
  feedSearch: null,
  xfQ: "",
  xfScope: "follows",

  nav(id, arg = null) {
    // Signed out, only the public screens exist; anything else is the door.
    if (get().sessionLoaded && !get().user && !PUBLIC_SCREENS.has(id)) {
      id = "welcome";
      arg = null;
    }
    const { screen, arg: cur, back } = get();
    if (screen !== id || cur !== arg) {
      const next = [...back, { s: screen, a: cur }];
      if (next.length > 30) next.shift();
      set({ back: next });
      history.pushState(null, "", pathFor(id, arg));
    }
    set({
      screen: id,
      arg,
      menuOpen: false,
      searchOpen: false,
      sheet: null,
      popover: null,
      // Surface search resets on every entry: the scope must be a fresh,
      // explicit choice each visit, never a leftover.
      discoverQ: "",
      discoverScope: "follows",
      discoverExpert: null,
      discoverSourceBefore: null,
      practiceSearch: null,
      feedQ: "",
      feedScope: "follows",
      feedSourceBefore: null,
      feedSearch: null,
      xfQ: "",
      xfScope: "follows",
    });
    window.scrollTo(0, 0);
  },

  goBack(fallback = "today", fallbackArg = null) {
    const { back } = get();
    const prev = back.at(-1);
    if (prev) {
      set({ back: back.slice(0, -1), screen: prev.s, arg: prev.a });
      history.pushState(null, "", pathFor(prev.s, prev.a));
    } else {
      set({ screen: fallback, arg: fallbackArg });
      history.pushState(null, "", pathFor(fallback, fallbackArg));
    }
    set({ menuOpen: false, searchOpen: false, sheet: null, popover: null });
    window.scrollTo(0, 0);
  },

  flipTheme() {
    const next: Theme = get().theme === "dark" ? "light" : "dark";
    set({ theme: next });
    try {
      localStorage.setItem("practice-theme", next);
    } catch {
      // Private mode: the toggle still works for the session.
    }
    // The preference lives on the user once there is one.
    if (get().user) {
      void postJson("/api/account/theme", { theme: next }).catch(() => undefined);
    }
  },

  // null returns the zone to automatic: the device decides, now and at
  // every future boot.
  async setTimezone(tz) {
    const user = get().user;
    if (!user) return;
    const next = tz ?? (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
    const source = tz ? "user" : "auto";
    set({ user: { ...user, timezone: next, timezoneSource: source } });
    try {
      await postJson("/api/account/timezone", { timezone: next, source });
    } catch {
      set({ user });
      return;
    }
    // The day may have moved; recompute suggestions, the ring, the week.
    await get().refreshState();
  },

  setMenu(open) {
    set({ menuOpen: open });
  },
  setSearchOpen(open) {
    set({ searchOpen: open });
  },
  openSheet(sheet, under) {
    set({ sheet, popover: null, sheetUnder: under ?? null, sheetScroll: null });
  },
  closeSheet() {
    // Arriving on /terms leaves that address showing while the modal is up,
    // which is what makes the link shareable and the back button work. Once
    // the document is closed the address bar would be describing a page that
    // is no longer on screen, so it goes back to the screen underneath.
    // Replaced, not pushed: closing is not a place in the history.
    if (isDocPath(location.pathname)) {
      history.replaceState(null, "", pathFor(get().screen, get().arg));
    }
    // Back to whatever sent us here, at the scroll it was left at.
    const under = get().sheetUnder;
    if (under) {
      set({ sheet: under.sheet, sheetUnder: null, sheetScroll: under.scroll });
      return;
    }
    set({ sheet: null, sheetScroll: null });
  },
  clearSheetScroll() {
    set({ sheetScroll: null });
  },
  openPopover(p) {
    set({ popover: p });
  },
  closePopover() {
    set({ popover: null });
  },
  setDiscTab(t) {
    set({ discTab: t });
  },
  // Changing domain drops any topic refinement, otherwise picking Nutrition
  // then Sleep would leave a topic filter from the wrong domain.
  setPlanDomain(d) {
    set({ planDomain: d, planTopic: null });
  },
  setPlanTopic(t) {
    set({ planTopic: t });
  },
  // Choosing a tab by hand takes control back from a deep link, otherwise
  // the focused question would re-force its own tab on every repaint.
  setXfTab(t) {
    set({ xfTab: t, arg: null });
  },
  setFollowCat(c) {
    set({ followCat: c });
  },
  setFollowQ(q) {
    set({ followQ: q });
  },
  setFeedFilter(f) {
    set({ feedFilter: f });
  },
  // Only a typed query opens the search view, and leaving it puts the
  // source back exactly as it was on the way in (31 Jul 2026, Rob). The
  // source you named while searching belongs to that search; the one you
  // had chosen before it belongs to the page, and the page gets it back.
  setDiscoverQ(q) {
    const was = get().discoverQ.trim() !== "";
    const now = q.trim() !== "";
    if (!was && now) set({ discoverSourceBefore: get().discoverExpert });
    if (was && !now) {
      set({ discoverExpert: get().discoverSourceBefore, practiceSearch: null });
    }
    set({ discoverQ: q });
  },
  // Flipping scope or naming a source re-runs a live search immediately:
  // these controls are part of the results, not settings you save.
  setDiscoverScope(s) {
    set({ discoverScope: s });
    if (get().discoverQ.trim()) void get().runPracticeSearch();
  },
  // Naming a source no longer starts a search on its own (31 Jul 2026,
  // Rob): with no query the reader stays on the lanes, which narrow to that
  // person in place. The server is only asked once there is something to
  // ask it.
  setDiscoverExpert(id) {
    set({ discoverExpert: id });
    if (get().discoverQ.trim()) void get().runPracticeSearch();
    else set({ practiceSearch: null });
  },
  // Same rule as Discover's: the source you had before the search is the
  // source you get back after it. setFeedExpert does the refetch.
  setFeedQ(q) {
    const was = get().feedQ.trim() !== "";
    const now = q.trim() !== "";
    if (!was && now) set({ feedSourceBefore: get().feedExpert });
    set({ feedQ: q, ...(now ? {} : { feedSearch: null }) });
    if (was && !now) get().setFeedExpert(get().feedSourceBefore);
  },
  setFeedScope(s) {
    set({ feedScope: s });
    if (get().feedQ.trim()) void get().runFeedSearch();
  },
  setXfQ(q) {
    set({ xfQ: q });
  },
  setXfScope(s) {
    set({ xfScope: s });
  },

  async runPracticeSearch() {
    const q = get().discoverQ.trim();
    const expert = get().discoverExpert;
    if (!q && !expert) {
      set({ practiceSearch: null });
      return;
    }
    try {
      const params = new URLSearchParams({ q, scope: get().discoverScope });
      if (expert) params.set("expert", expert);
      const res = await fetch(`/api/search/practices?${params.toString()}`);
      // Stale responses lose: only the answer to the current ask lands.
      if (res.ok && get().discoverQ.trim() === q && get().discoverExpert === expert) {
        set({ practiceSearch: (await res.json()) as PracticeSearch });
      }
    } catch {
      // Screen keeps its previous results; the next keystroke retries.
    }
  },

  async runFeedSearch() {
    const q = get().feedQ.trim();
    if (!q) {
      set({ feedSearch: null });
      return;
    }
    const expert = get().feedExpert;
    try {
      const params = new URLSearchParams({ q, scope: get().feedScope });
      if (expert) params.set("expert", expert);
      const res = await fetch(`/api/search/feed?${params.toString()}`);
      if (res.ok && get().feedQ.trim() === q && get().feedExpert === expert) {
        const body = (await res.json()) as { items: FeedItemView[] };
        set({ feedSearch: body.items });
      }
    } catch {
      // Screen keeps its previous results; the next keystroke retries.
    }
  },

  toggleArea(id) {
    const ob = get().onboarding;
    const areas = { ...ob.areas, [id]: !ob.areas[id] };
    set({ onboarding: { ...ob, areas, topics: seedTopics(areas, ob.topics, ob.touched) } });
  },
  toggleObTopic(id) {
    const ob = get().onboarding;
    const touched = ob.touched.includes(id) ? ob.touched : [...ob.touched, id];
    set({ onboarding: { ...ob, touched, topics: { ...ob.topics, [id]: !ob.topics[id] } } });
  },
  toggleObFollow(id) {
    const ob = get().onboarding;
    const follows = ob.follows.includes(id)
      ? ob.follows.filter((f) => f !== id)
      : [...ob.follows, id];
    set({ onboarding: { ...ob, follows } });
  },

  // The plan is already built when the address is asked for; stash it so
  // the magic-link round-trip (a fresh page load) can save it server-side.
  async finishOnboarding(email, inviteCode) {
    try {
      localStorage.setItem(ONBOARDING_STASH, JSON.stringify(get().onboarding));
    } catch {
      // Private mode: onboarding still completes, answers reapply in-app.
    }
    const result = await api.requestLink(email, inviteCode);
    if (result.ok) set({ linkSent: email });
    return result;
  },

  async requestSignIn(email) {
    const result = await api.requestLink(email, undefined, "signin");
    if (result.ok) set({ linkSent: email });
    return result;
  },

  async refreshState() {
    try {
      const res = await fetch("/api/state");
      if (res.ok) set({ appState: (await res.json()) as StatePayload });
    } catch {
      // Kept stale; the next mutation refreshes.
    }
  },

  async mutate(url, body) {
    try {
      const payload = (await postJson(url, body)) as StatePayload;
      if (payload && "counts" in payload) set({ appState: payload });
    } catch {
      await get().refreshState();
    }
  },

  async loadCrossfire() {
    try {
      const res = await fetch("/api/crossfire");
      if (res.ok) {
        const body = (await res.json()) as { questions: CrossfireQuestionView[] };
        set({ crossfire: body.questions });
      }
    } catch {
      // Screen shows its empty state.
    }
  },

  async loadFeed() {
    try {
      const ex = get().feedExpert;
      // The full window the server allows: pagination is client-side, and
      // Load more should not run dry at a smaller default. The nav badge
      // counts unread over this same window, so the constant is shared.
      const res = await fetch(`/api/feed?limit=${FEED_WINDOW}${ex ? `&expert=${encodeURIComponent(ex)}` : ""}`);
      if (res.ok) {
        set({ feed: (await res.json()) as { items: FeedItemView[]; saved: string[]; read: string[] } });
      }
    } catch {
      // Screen shows its empty state.
    }
  },

  setFeedExpert(id) {
    if (get().feedExpert === id) return;
    set({ feedExpert: id, feed: null });
    void get().loadFeed();
    // A live search follows the picker too.
    if (get().feedQ.trim()) void get().runFeedSearch();
  },

  async loadCardDetail(id) {
    if (get().cardDetails[id]) return;
    try {
      const res = await fetch(`/api/cards/${id}`);
      if (res.ok) {
        const detail = (await res.json()) as CardDetail;
        set({ cardDetails: { ...get().cardDetails, [id]: detail } });
      }
    } catch {
      // Screen shows its empty state.
    }
  },

  async loadExpertDetail(id) {
    if (get().expertDetails[id]) return;
    try {
      const res = await fetch(`/api/experts/${id}`);
      if (res.ok) {
        const detail = (await res.json()) as ExpertDetail;
        set({ expertDetails: { ...get().expertDetails, [id]: detail } });
      }
    } catch {
      // Screen shows its empty state.
    }
  },

  async addCard(cardId, version, when) {
    await get().mutate("/api/plan/add", { cardId, version, when });
    // Adding from the practice modal lands you back on the surface behind
    // it, where the slot now carries the added state.
    set({ sheet: null });
  },
  async toggleComplete(planItemId, date, done) {
    await get().mutate("/api/plan/complete", { planItemId, date, done });
  },
  async markAll(planItemIds, date, done) {
    await get().mutate("/api/plan/mark-all", { planItemIds, date, done });
  },
  // Applied locally before the round trip, unlike every other mutation here:
  // a card that springs back to where it was and only settles when the
  // server answers reads as a failed drag. The server returns the whole
  // payload as usual and overwrites this a moment later.
  async reorderPlan(planItemIds) {
    const s = get().appState;
    if (s) {
      // Only the moved rows change places, and they land in the slots those
      // same rows already occupied, so a reorder inside a filtered list
      // leaves everything it could not see exactly where it was.
      const moved = new Set(planItemIds);
      const queue = planItemIds
        .map((id) => s.plan.find((p) => p.id === id))
        .filter((p): p is PlanItemView => p !== undefined);
      let n = 0;
      const plan = s.plan.map((p) => (moved.has(p.id) ? (queue[n++] ?? p) : p));
      set({ appState: { ...s, plan } });
    }
    await get().mutate("/api/plan/reorder", { planItemIds });
  },
  async snoozeCard(cardId, duration, reason, planItemId) {
    if (planItemId) await get().mutate("/api/plan/status", { planItemId, status: "stopped" });
    await get().mutate("/api/practices/snooze", { cardId, duration, reason });
    // A decision made from the practice modal lands you back on the
    // surface behind it; with no sheet open this is a no-op.
    set({ sheet: null });
  },
  async unsnooze(cardId) {
    await get().mutate("/api/practices/unsnooze", { cardId });
  },
  async declineCard(cardId, scope, reason, planItemId) {
    if (planItemId) await get().mutate("/api/plan/status", { planItemId, status: "stopped" });
    await get().mutate("/api/practices/decline", { cardId, scope, reason });
    set({ sheet: null });
  },
  async undecline(declineId) {
    await get().mutate("/api/practices/undecline", { declineId });
  },

  // Later for your own practice: a break, not a delete. Status paused plus
  // a return date; buildState brings it back exactly once, on its date.
  async pausePlan(planItemId, duration) {
    await get().mutate("/api/plan/pause", { planItemId, duration });
    set({ sheet: null });
  },
  async createPractice(fields) {
    try {
      const payload = (await postJson("/api/practices/create", fields)) as StatePayload & {
        created?: string;
      };
      if (payload && "counts" in payload) set({ appState: payload });
      return payload.created ?? null;
    } catch {
      await get().refreshState();
      return null;
    }
  },
  async updatePractice(fields) {
    await get().mutate("/api/practices/update", fields);
    // The detail record is session-cached; an edit makes it stale.
    const { [fields.cardId]: _stale, ...rest } = get().cardDetails;
    set({ cardDetails: rest });
  },
  async removePractice(cardId) {
    await get().mutate("/api/practices/remove", { cardId });
    const { [cardId]: _stale, ...rest } = get().cardDetails;
    set({ cardDetails: rest, sheet: null });
  },
  async uploadPracticeImage(cardId, file) {
    try {
      const form = new FormData();
      form.append("cardId", cardId);
      form.append("file", file);
      const res = await fetch("/api/practices/image", { method: "POST", body: form });
      if (!res.ok) return false;
      const payload = (await res.json()) as StatePayload;
      if (payload && "counts" in payload) set({ appState: payload });
      const { [cardId]: _stale, ...rest } = get().cardDetails;
      set({ cardDetails: rest });
      return true;
    } catch {
      return false;
    }
  },
  async setAreas(areas) {
    await get().mutate("/api/account/areas", { areas });
  },
  async reportIssue(subjectType, subjectId, reason, detail) {
    try {
      await postJson("/api/report", { subjectType, subjectId, reason, detail });
      return true;
    } catch {
      return false;
    }
  },
  async setTopic(topicId, on) {
    await get().mutate("/api/controls/topic", { topicId, on });
  },
  async setMute(expertId, topicId, muted) {
    await get().mutate("/api/controls/mute", { expertId, topicId, muted });
  },

  // Following follows, nothing more (28 Jul 2026): the per-expert switches
  // live on the profile now, so there is no screen to bounce to. Unfollow
  // just deactivates; mutes survive.
  toggleFollow(expertId) {
    const s = get();
    const following = s.appState?.follows.includes(expertId) ?? false;
    void s.mutate("/api/follows", { expertId, active: !following });
  },

  async pickStance(questionId, pick, note = null) {
    const s = get();
    const q = s.crossfire?.find((x) => x.id === questionId);
    const last = q?.history.at(-1);
    if (last && last.pick === pick) return;
    let label: string;
    const name = (id: string) => s.appState?.experts[id]?.short ?? s.appState?.experts[id]?.name ?? id;
    if (pick === "open") label = last ? "Back to still deciding" : "Marked still deciding";
    else label = last && last.pick !== "open" ? `Changed to ${name(pick)}` : `Went with ${name(pick)}`;
    await s.mutate("/api/stances", { questionId, pick, label, note });
    await s.loadCrossfire();
  },

  async saveFeedItem(feedItemId, saved) {
    await postJson("/api/feed/save", { feedItemId, saved }).catch(() => undefined);
    const feed = get().feed;
    if (feed) {
      set({
        feed: {
          ...feed,
          saved: saved ? [...feed.saved, feedItemId] : feed.saved.filter((i) => i !== feedItemId),
        },
      });
    }
  },

  async markFeedRead(feedItemId, read) {
    await postJson("/api/feed/read", { feedItemId, read }).catch(() => undefined);
    const feed = get().feed;
    if (feed) {
      set({
        feed: {
          ...feed,
          read: read ? [...feed.read, feedItemId] : feed.read.filter((i) => i !== feedItemId),
        },
      });
    }
  },

  async setPlanVersion(planItemId, version) {
    await get().mutate("/api/plan/version", { planItemId, version });
  },
  async setPlanStatus(planItemId, status) {
    await get().mutate("/api/plan/status", { planItemId, status });
  },

  async boot() {
    const [user, experts] = await Promise.all([
      api.session().catch(() => null),
      api.experts().catch(() => null),
    ]);
    set({ user, experts, sessionLoaded: true });
    // First signed-in boot after onboarding: flush the stashed answers
    // before the state fetch, so the first paint has the follows.
    if (user) {
      // The account's day follows the browser's timezone until the user
      // picks one in settings. Synced before the state fetch so Today is
      // computed on the right day the first time, not after a reload.
      try {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserTz && user.timezoneSource === "auto" && user.timezone !== browserTz) {
          await postJson("/api/account/timezone", { timezone: browserTz, source: "auto", onlyIfAuto: true });
          set({ user: { ...user, timezone: browserTz } });
        }
      } catch {
        // The server keeps its date; nothing else depends on this call.
      }
      try {
        const raw = localStorage.getItem(ONBOARDING_STASH);
        if (raw) {
          const ob = JSON.parse(raw) as OnboardingState;
          await postJson("/api/onboarding", {
            areas: Object.keys(ob.areas).filter((k) => ob.areas[k]),
            topics: ob.topics,
            follows: ob.follows,
          });
          localStorage.removeItem(ONBOARDING_STASH);
        }
      } catch {
        // Left in place; the next boot retries.
      }
      await get().refreshState();
      // The stored theme wins until sign-in; after it, the account's.
      const theme = user.theme === "dark" ? "dark" : "light";
      set({ theme });
    }
    const { screen } = get();
    if (!user && !PUBLIC_SCREENS.has(screen)) {
      history.replaceState(null, "", pathFor("welcome", null));
      set({ screen: "welcome", arg: null, back: [] });
    }
  },

  async signOut() {
    await api.signOut();
    set({ user: null, appState: null, crossfire: null, feed: null, back: [] });
    history.replaceState(null, "", pathFor("welcome", null));
    set({ screen: "welcome", arg: null });
  },
}));

// Browser back and forward mirror goBack: parse the URL and set the screen
// without pushing new history.
window.addEventListener("popstate", () => {
  // Same resolution as the first load, so stepping back onto /terms opens the
  // terms again rather than landing on a screen with no document.
  const frame = landingFor(location.pathname);
  const { back, user, sessionLoaded } = useStore.getState();
  if (sessionLoaded && !user && !PUBLIC_SCREENS.has(frame.screen)) {
    frame.screen = "welcome";
    frame.arg = null;
  }
  const top = back.at(-1);
  useStore.setState({
    screen: frame.screen,
    arg: frame.arg,
    back: top && top.s === frame.screen && top.a === frame.arg ? back.slice(0, -1) : back,
    menuOpen: false,
    searchOpen: false,
    sheet: frame.sheet,
    popover: null,
  });
});
